import { CheckCircle2, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/lib/AuthProvider'

type Plan = {
  id: string
  name: string
  description: string
  price: number
  features_json: string[]
}

const defaultPlans: Plan[] = [
  {
    id: 'basico',
    name: 'Básico',
    description: 'Para lava-rápidos que estão profissionalizando a gestão.',
    price: 69.9,
    features_json: [
      'Painel Kanban de OS',
      'Controle de Caixa Simples',
      'Cadastro de Clientes',
      'Suporte via Email',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Para estéticas automotivas de alto fluxo e exigência.',
    price: 129.9,
    features_json: [
      'Tudo do plano Básico',
      'Caixa Cego (Antifraude)',
      'Módulo de Comissões',
      'Suporte WhatsApp',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Para redes e franquias que tomam decisões baseadas em dados.',
    price: 199.9,
    features_json: [
      'Tudo do plano Pro',
      'AI Growth Dashboard',
      'Pesquisas NPS',
      'Gerente de Conta Dedicado',
    ],
  },
]

export function UpgradeModal({
  isOpen,
  onClose,
  companyId,
}: {
  isOpen: boolean
  onClose: () => void
  companyId: string
}) {
  const { user } = useAuth()
  const [plans, setPlans] = useState<Plan[]>(defaultPlans)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)

  useEffect(() => {
    async function loadPlans() {
      const { data, error } = await supabase
        .from('system_plans')
        .select('id, name, description, price, features_json')
        .eq('status', 'ACTIVE')
        .order('price', { ascending: true })
      if (data && !error && data.length > 0) {
        setPlans(data as Plan[])
      }
    }
    if (isOpen) loadPlans()
  }, [isOpen])

  const handleCheckout = async (plan: Plan) => {
    if (!user) return
    setIsLoading(true)
    setSelectedPlanId(plan.id)

    try {
      // Pega dados da empresa (CNPJ e nome) para gerar fatura
      const { data: companyData } = await supabase
        .from('company_settings')
        .select('name, cnpj')
        .eq('id', companyId)
        .single()

      const res = await supabase.functions.invoke('asaas-checkout', {
        body: {
          planId: plan.id,
          companyId: companyId,
          email: user.email,
          name: companyData?.name || user.user_metadata?.name || 'Cliente',
          cpfCnpj: companyData?.cnpj || '00000000000', // O Asaas precisa de um CPF/CNPJ válido
        },
      })

      if (res.error) throw new Error(res.error.message || 'Erro ao gerar fatura Asaas.')

      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl
      }
    } catch (err: any) {
      console.error(err)
      alert(`Erro ao processar assinatura: ${err.message}`)
      setIsLoading(false)
      setSelectedPlanId(null)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isLoading && onClose()}>
      <DialogContent className="max-w-5xl bg-slate-950 text-slate-50 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Escolha seu Plano</DialogTitle>
          <DialogDescription className="text-slate-400">
            Assine para manter seu Lava-Jato organizado e lucrando mais.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {plans.map((plan, index) => {
            const isHighlight = index === 1 // Pro
            const isPremium = index === 2 // Premium
            const isProcessingThis = isLoading && selectedPlanId === plan.id

            return (
              <div
                key={plan.id}
                className={`rounded-2xl p-6 flex flex-col relative ${
                  isHighlight
                    ? 'bg-gradient-to-b from-blue-900/40 to-slate-900 border border-blue-500/50 shadow-lg shadow-blue-500/10'
                    : 'bg-slate-900 border border-slate-800'
                }`}
              >
                {isHighlight && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg">
                    MAIS ESCOLHIDO
                  </div>
                )}
                <h3
                  className={`text-xl font-bold mb-1 ${isPremium ? 'text-purple-400' : isHighlight ? 'text-blue-400' : 'text-white'}`}
                >
                  {plan.name}
                </h3>
                <p className="text-slate-400 text-xs mb-4 min-h-[32px]">{plan.description}</p>
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-extrabold text-white">R$ {plan.price}</span>
                    <span className="text-slate-500 text-sm">/mês</span>
                  </div>
                </div>
                <div className="space-y-2 mb-6 flex-1">
                  {plan.features_json.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2 text-slate-300 text-xs">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => handleCheckout(plan)}
                  disabled={isLoading}
                  className={`w-full font-bold ${
                    isHighlight
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : isPremium
                        ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-800/50 border border-purple-500/50'
                        : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                  }`}
                >
                  {isProcessingThis ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Processando...
                    </span>
                  ) : (
                    'Assinar'
                  )}
                </Button>
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
